import { useEffect, useRef, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { KINGDOMS, QUESTS, ghosts } from '@yuan-math/content';
import {
  bestiaryEntries,
  ghostIndex,
  isUnseen,
  journalEntries,
  type JournalChallenge,
  type JournalConquered,
  type JournalEntry,
  type BestiaryEntry,
} from '@yuan-math/world';
import { journalSeenOf, namedTricksOf } from '@yuan-math/learning';
import { Card, EmptyState, SectionHeading } from '@yuan-math/ui';
import { registry, useLearner } from '../../state/LearnerContext.tsx';
import { MasteryBand } from '../../components/MasteryBand.tsx';

/**
 * `/adventure/journal` — the Adventure Journal (docs/rpg.md §18).
 *
 * "Mistakes should become part of the learning journey." The book is the whole
 * of that idea: every wrong answer the student has given lately is already in
 * the profile, and every wrong answer worth naming already carries a
 * misconception with a plain-words description and the concept that repairs it.
 * §18's example is what this page renders — *encountered 4 times · the skill ·
 * the common mistake · the recommended quest* — and it renders nothing else.
 *
 * **This page measures nothing.** `journalEntries` in `@yuan-math/world`
 * derives every word of it, using the recommender's own definition of who is
 * struggling, so the journal and the home page can never disagree about how the
 * student is doing. Reading a page of it changes no mastery, no streak and no
 * XP; the single thing stored is that the entry has been *seen*.
 *
 * **It is the learner's, and nobody else's.** A journal is a log of every
 * answer with the story turned up, and a log of every answer is precisely what
 * the parent, teacher and admin views are forbidden to show (CLAUDE.md §14,
 * §29) — a student who believes every keystroke is being read stops guessing,
 * and guessing is how mathematics is learned. So nothing here is exported to
 * any shared surface, and the route is `noindex` like the rest of `/adventure`.
 *
 * **The tone is the feature.** A challenge is written as a thing to go and beat,
 * never as a failing; a conquered one is written as the comeback it is. The
 * empty state welcomes rather than apologises — an adventurer with no scars has
 * not been anywhere yet.
 */

const conceptTitle = (id: string): string => registry.concept(id)?.title ?? id;

/** The bestiary is a pure function of static content, so it is built once. */
const BESTIARY = ghostIndex(ghosts);

/** "4 times", said the way a person would say it. */
function timesPhrase(count: number): string {
  if (count === 1) return 'once';
  if (count === 2) return 'twice';
  return `${count} times`;
}

/** The dot on an entry the student has not read yet. A dot *and* a word, never colour alone. */
function NewMark(): JSX.Element {
  return (
    <span className="journal-entry__new">
      <span className="journal-entry__dot" aria-hidden="true" />
      New
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* The two kinds of page                                                      */
/* -------------------------------------------------------------------------- */

function ChallengeEntry({ entry, unseen }: { entry: JournalChallenge; unseen: boolean }): JSX.Element {
  const title = conceptTitle(entry.conceptId);
  return (
    <li className="journal-entry journal-entry--challenge">
      <p className="journal-entry__count">
        <span aria-hidden="true">📖</span> You have met this challenge {timesPhrase(entry.encounters)}.
        {unseen ? <NewMark /> : null}
      </p>

      <h3 className="journal-entry__skill">
        <span className="journal-entry__label">Skill</span>
        <Link to={`/concepts/${entry.conceptId}`}>{title}</Link>
      </h3>

      {entry.description ? (
        <p className="journal-entry__mistake">
          <span className="journal-entry__label">Common mistake</span>
          {entry.description}
        </p>
      ) : (
        /*
         * No misconception matched, which is a real outcome and not a gap to
         * paper over: the answers were wrong in no pattern the catalog knows.
         * Naming one anyway would be the platform inventing a diagnosis.
         */
        <p className="journal-entry__mistake">
          <span className="journal-entry__label">Common mistake</span>
          Nothing regular yet — the misses have not fallen into a pattern. More practice will show what is going on.
        </p>
      )}

      <p className="journal-entry__next">
        <span className="journal-entry__label">{entry.questId ? 'Recommended quest' : 'Recommended practice'}</span>
        <Link to={entry.href} className="journal-entry__link">
          <span aria-hidden="true">{entry.questId ? '⚔️' : '🎯'}</span>{' '}
          {entry.questId
            ? QUESTS.find((quest) => quest.id === entry.questId)?.title ?? 'A quest awaits'
            : `Practise ${conceptTitle(entry.remediationConceptId ?? entry.conceptId)}`}
        </Link>
      </p>
    </li>
  );
}

function ConqueredEntry({ entry, unseen }: { entry: JournalConquered; unseen: boolean }): JSX.Element {
  const title = conceptTitle(entry.conceptId);
  return (
    <li className="journal-entry journal-entry--conquered">
      <p className="journal-entry__count">
        <span aria-hidden="true">🏅</span> You once struggled with{' '}
        <Link to={`/concepts/${entry.conceptId}`}>{title}</Link>. Mastered.
        {unseen ? <NewMark /> : null}
      </p>
      <p className="journal-entry__scar">
        It cost you {timesPhrase(entry.struggledCount)} wrong, and then it stopped costing you anything.
      </p>
      <MasteryBand level={entry.masteryLevel} />
    </li>
  );
}

/**
 * One creature the student has actually met.
 *
 * The same material as a challenge entry, organised the other way round: a
 * challenge is *a concept that keeps going wrong*, and a ghost is *a mistake
 * that keeps recurring* — across concepts, across kingdoms and across years,
 * which is the thing the concept-shaped view cannot show. The Wraith of Common
 * Denominators is one page whether it caught you in Grade 4 fractions or in
 * Grade 9 rational expressions.
 *
 * No bar, no percentage and no red: an encounter count is what has been coming
 * up lately, not a score. Sealed is a lock *and* the word, never a colour.
 */
function BestiaryRow({ entry }: { entry: BestiaryEntry }): JSX.Element {
  const { ghost } = entry;
  return (
    <li className="bestiary__entry">
      <span className="bestiary__emoji" aria-hidden="true">
        {ghost.emoji}
      </span>
      <div className="bestiary__body">
        <p className="bestiary__name">{ghost.name}</p>
        <p className="bestiary__meta">
          Met {timesPhrase(entry.encounters)}
          {entry.sealed ? (
            <>
              {' · '}
              <span className="bestiary__sealed">
                <span aria-hidden="true">🔒</span> Sealed
              </span>
            </>
          ) : (
            ' · not yet named'
          )}
        </p>
        <p className="bestiary__ward">
          Warded by <Link to={`/concepts/${ghost.wardConceptId}`}>{ghost.wardTitle}</Link>
        </p>
      </div>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* The page                                                                   */
/* -------------------------------------------------------------------------- */

export function JournalPage(): JSX.Element {
  const { profile, markJournalSeen } = useLearner();

  // Derived per render, like mastery everywhere else: retention decays, so
  // whether a challenge has been conquered is a question about this moment.
  const now = Date.now();
  const entries: JournalEntry[] = profile ? journalEntries(profile, { kingdoms: KINGDOMS, quests: QUESTS }, now) : [];

  /*
   * What was new when the page opened, frozen.
   *
   * Marking runs on mount, so reading the live `journalSeen` would clear every
   * dot in the same paint that drew it — the student would never see which
   * entry was the new one. The snapshot is taken once and the marking happens
   * underneath it.
   */
  const seenOnOpen = useRef<readonly string[] | null>(null);
  // Taken on the first render that *has* a profile: a stored one arrives an
  // effect later, and a snapshot taken before it would call every entry new.
  if (seenOnOpen.current === null && profile) seenOnOpen.current = journalSeenOf(profile);
  const wasSeen = seenOnOpen.current ?? [];

  // Keyed on the ids themselves rather than the array, which is rebuilt every
  // render: the effect must fire when the *journal* changes, not when the clock
  // above it moves on.
  const entryKey = entries.map((entry) => entry.id).join('|');
  useEffect(() => {
    const ids = entryKey === '' ? [] : entryKey.split('|');
    if (ids.length > 0) markJournalSeen(ids);
  }, [entryKey, markJournalSeen]);

  if (!profile) return <div />;

  const challenges = entries.filter((entry): entry is JournalChallenge => entry.kind === 'challenge');
  const conquered = entries.filter((entry): entry is JournalConquered => entry.kind === 'conquered');

  /*
   * The bestiary is the journal's own material seen from the other side: the
   * challenges above are organised by *concept*, and these by the *mistake* —
   * which is what recurs across concepts and across years. Derived from the
   * same attempt log, plus the one stored fact the log cannot hold: which
   * tricks the student has correctly named.
   */
  const bestiary = bestiaryEntries(BESTIARY, profile.attempts, namedTricksOf(profile));

  return (
    <div className="page page--narrow journal-page">
      <h1 className="page__title">Adventure journal</h1>
      <p className="journal-page__lead">
        Every adventurer keeps one. This is what has been going wrong lately, what it usually turns out to be, and where
        to go and settle it — written from your recent practice, and read by nobody but you.
      </p>

      {entries.length === 0 ? (
        <Card className="journal-book journal-book--empty">
          <EmptyState
            /* The only heading on an empty journal, so it is the section the
               `h1` above it introduces rather than a subsection of nothing. */
            headingLevel={2}
            title="Your journal is waiting for its first story"
            description="Adventurers collect scars. Go and answer something hard — the first page writes itself the moment a problem puts up a fight."
            action={
              <Link to="/adventure" className="btn btn--primary">
                Back to the adventure
              </Link>
            }
          />
        </Card>
      ) : null}

      {challenges.length > 0 ? (
        <section className="journal-section">
          <SectionHeading
            title="Open challenges"
            description="Not a report card — a to-do list. Each one names what is going wrong and the shortest way through it."
          />
          <Card className="journal-book">
            <ul className="journal-entries">
              {challenges.map((entry) => (
                <ChallengeEntry key={entry.id} entry={entry} unseen={isUnseen(entry, wasSeen)} />
              ))}
            </ul>
          </Card>
        </section>
      ) : null}

      {conquered.length > 0 ? (
        <section className="journal-section">
          <SectionHeading
            title="Conquered"
            description="Skills that used to go wrong and do not any more. This is the half of the story worth re-reading."
          />
          <Card className="journal-book">
            <ul className="journal-entries">
              {conquered.map((entry) => (
                <ConqueredEntry key={entry.id} entry={entry} unseen={isUnseen(entry, wasSeen)} />
              ))}
            </ul>
          </Card>
        </section>
      ) : null}

      {bestiary.length > 0 ? (
        <section className="journal-section">
          <SectionHeading
            title="Bestiary"
            description="The same mistakes, wearing the shapes the adventure gives them. A ghost is sealed once you can name its trick."
          />
          <Card className="journal-book">
            <ul className="bestiary">
              {bestiary.map((entry) => (
                <BestiaryRow key={entry.ghost.id} entry={entry} />
              ))}
            </ul>
          </Card>
        </section>
      ) : null}

      {/*
       * Stated, not buried. The attempt log is bounded (ATTEMPT_LOG_LIMIT), so
       * this book holds recent history rather than everything that ever
       * happened — and a page that quietly loses old entries without saying so
       * would read as the platform forgetting the student's worst week on
       * purpose.
       */}
      <p className="journal-page__note">
        The journal keeps your recent practice rather than everything you have ever answered, so old entries fade out of
        it as you work. Nothing you have learned fades with them — that lives on the map.
      </p>

      <div className="button-row">
        <Link to="/adventure" className="btn btn--secondary">
          Back to the adventure
        </Link>
        <Link to="/adventure/map" className="btn btn--secondary">
          World map
        </Link>
      </div>
    </div>
  );
}
